import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import { listCouncils, listModels, refreshModels, type Council, type Model } from './api'
import { CATALOGUE_STATUS_LABEL, STAGE_KIND_LABEL } from './vocab'

/**
 * Which models this deployment may use, and whether they are real.
 *
 * ## The catalogue ships as a snapshot and starts going stale that day
 *
 * Model ids and prices change monthly. “Check against the providers” asks each
 * vendor for its own model list and marks every row VERIFIED or
 * MISSING_AT_PROVIDER with a date — turning “these numbers might be wrong” from a
 * caveat in a README into a column somebody can sort by.
 *
 * It disables nothing. A model missing from a provider's list is usually a
 * rename, and taking it away from a council that is using it would be a worse
 * error than leaving a stale row with a warning on it.
 *
 * ## `supportsSampling` is a real column, not defensive coding
 *
 * `temperature` and `top_p` are rejected with a 400 by the current Anthropic
 * frontier models. A request builder that always sends one cannot use the best
 * models available to it, and the failure is a 400 nobody reads. The column is
 * why this deployment can.
 */
export function ModelsPage() {
  const [models, setModels] = useState<Model[] | null>(null)
  const [councils, setCouncils] = useState<Council[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const mayConfigure = useCan('advisor:configure')

  const load = useCallback(async () => {
    try {
      setError(null)
      const [m, c] = await Promise.all([listModels(), listCouncils()])
      setModels(m)
      setCouncils(c)
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const refresh = async () => {
    setBusy(true)
    setResult(null)
    try {
      const r = await refreshModels()
      setResult(`${r.providersChecked} provider(s) answered · ${r.verified} confirmed · `
        + `${r.missing} not listed` + (r.problems.length
          ? ` · could not reach: ${r.problems.join('; ')}` : ''))
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const vendors = useMemo(
    () => new Set((models ?? []).filter((m) => m.reachable).map((m) => m.vendor)),
    [models])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Models</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Credentials live in Integrations, never here. A model is reachable when an ACTIVE
            integration for its provider has a key stored against it.
          </p>
        </div>
        {mayConfigure && (
          <button type="button" onClick={() => void refresh()} disabled={busy}
            className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5" />}
            Check against the providers
          </button>
        )}
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {result && <Note tone="info" onDismiss={() => setResult(null)}>{result}</Note>}
      {models === null && !error && <LoadingBlock />}

      {models && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Catalogued" value={fmtNumber(models.length)} />
            <MetricCard label="Reachable"
              value={fmtNumber(models.filter((m) => m.reachable).length)} />
            <MetricCard label="Vendors configured" value={fmtNumber(vendors.size)}
              sub={vendors.size >= 2
                ? 'a red team can differ from the analyst'
                : 'a red team has nobody else to be'} />
            <MetricCard label="Unverified"
              value={fmtNumber(models.filter((m) => m.catalogueStatus === 'UNVERIFIED').length)} />
          </div>

          {models.filter((m) => m.reachable).length === 0 && (
            <Note tone="bad">
              No model has an account behind it. Register a provider under{' '}
              <Link to="/integrations" className="underline">Integrations</Link> — the provider
              code in the table below is the one to register — and store its API key.
            </Note>
          )}

          <section>
            <h2 className="text-erp-sm font-medium mb-1.5">Councils and what they would route to</h2>
            <div className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              {councils.map((council) => {
                // The routing note is shown ONCE per card, not per stage. When nothing is
                // configured every stage returns the same paragraph, and repeating it five
                // times buries the one line that says which stages resolved.
                const blocked = council.stages.filter((s) => !s.resolvedModel)
                const reason = blocked.map((s) => s.routingNote).find(Boolean)
                return (
                  <div key={council.code} className="rounded-md border border-border px-3 py-2.5">
                    <div className="text-erp-sm font-medium">{council.title}</div>
                    <p className="text-erp-xs text-muted-foreground mt-0.5">
                      {council.description}
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {council.stages.map((stage) => (
                        <li key={stage.key} className="text-erp-xs">
                          <span className="text-muted-foreground">
                            {STAGE_KIND_LABEL[stage.kind] ?? stage.kind}
                          </span>{' → '}
                          {stage.resolvedModel
                            ? <span>{stage.resolvedVendor} {stage.resolvedModel}</span>
                            : <span className="text-amber-700 dark:text-amber-400">
                                no model
                              </span>}
                        </li>
                      ))}
                    </ul>
                    {reason && (
                      <p className="mt-1.5 border-t border-border pt-1.5 text-erp-xs
                        text-amber-700 dark:text-amber-400">
                        {blocked.length === council.stages.length
                          ? reason
                          : `${blocked.length} of ${council.stages.length} stages cannot run: `
                            + reason}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </section>

          <section>
            <h2 className="text-erp-sm font-medium mb-1.5">The registry</h2>
            <div>
              <ErpTable head={
                <>
                  <Th>Model</Th>
                  <Th>Provider</Th>
                  <Th>Wire</Th>
                  <Th>Tier</Th>
                  <Th>Capabilities</Th>
                  <Th className="text-right">In / out per Mtok</Th>
                  <Th className="text-right">Context</Th>
                  <Th>Sampling</Th>
                  <Th>Catalogue</Th>
                </>
              }>
                {models.map((model) => (
                  <tr key={model.id} className={cn('border-t border-border',
                    !model.reachable && 'opacity-60')}>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        {model.reachable
                          ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                          : <XCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        <span>{model.displayName}</span>
                      </div>
                      <div className="text-erp-xs text-muted-foreground">{model.modelKey}</div>
                    </Td>
                    <Td className="text-erp-xs">{model.integrationCode ?? '—'}</Td>
                    <Td className="text-erp-xs text-muted-foreground">{model.wire}</Td>
                    <Td className="text-erp-xs">{model.tier}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {model.capabilities.map((c) => (
                          <span key={c} className="rounded border border-border px-1 py-px
                            text-erp-xs text-muted-foreground">
                            {c.toLowerCase().replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td className="text-right tabular-nums text-erp-xs">
                      ${model.inputCostPerMtok} / ${model.outputCostPerMtok}
                    </Td>
                    <Td className="text-right tabular-nums text-erp-xs">
                      {fmtNumber(model.contextTokens)}
                    </Td>
                    <Td className="text-erp-xs">
                      {model.supportsSampling
                        ? <span className="text-muted-foreground">accepts</span>
                        : <span title="temperature and top_p are a 400 on this model">
                            rejects
                          </span>}
                    </Td>
                    <Td>
                      <span className={cn('inline-flex items-center gap-1 text-erp-xs',
                        model.catalogueStatus === 'MISSING_AT_PROVIDER'
                          && 'text-amber-700 dark:text-amber-400')}>
                        {model.catalogueStatus === 'MISSING_AT_PROVIDER' && (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {CATALOGUE_STATUS_LABEL[model.catalogueStatus]}
                      </span>
                      {model.lastVerifiedAt && (
                        <div className="text-erp-xs text-muted-foreground/70">
                          {fmtDateTime(model.lastVerifiedAt)}
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </ErpTable>
            </div>
            <p className="mt-1.5 text-erp-xs text-muted-foreground">
              Prices are what this build was written against and are editable under
              advisor:configure. Run the provider check before believing any of them.
            </p>
          </section>
        </>
      )}
    </div>
  )
}
